/**
 * GP-368 — Resolution steps for each Billing Troubleshooting Reason.
 *
 * The Task Queue tells staff WHAT is wrong (the Troubleshooting Reason stamped on
 * the task) but never HOW to fix it — the pathway lived in a spreadsheet. This
 * module carries that workflow map into PHOX: given the reason string on a task it
 * returns the ordered steps, their target due dates, and what to do when a due date
 * passes. It is the single source of truth for the step content; the UI
 * (`TroubleshootingStepsPanel`) only renders what it finds here.
 *
 * SOURCE OF CONTENT — the "SLA Task Timeline" workbook attached to GP-368:
 *   - sheet "Troubleshooting Reasons" — the Primary Reason / Subtype tree and the
 *     Task #1 → outcome → Task #2 → outcome → Task #3 chain.
 *   - sheet 2 — Task Definition / Task Due Date / Action after due date. The
 *     wording of `action`, `due` and `ifNotResolved` below is transcribed from
 *     those columns.
 *
 * DUE DATES ARE SHOWN VERBATIM ("Today +1"), not computed into a calendar date: the
 * workbook anchors them on the *patient's* due date ("Phox to calculate task due
 * date based on Task definition + Patient due date"), and a task carries no such
 * anchor field yet. Rendering an offset the reader can apply beats rendering a date
 * we'd have to guess.
 *
 * REASONS WITH NO PLAYBOOK render no panel at all. The workbook covers seven primary
 * reasons; Copay Outstanding, Too Early to Bill, Missing RX Image, Pending Delivery
 * Arrangement and the GP-1176 Wrong-* corrections are not among them. Inventing a
 * pathway would put made-up instructions in front of staff resolving a real
 * patient's claim — add the real steps here when pharmops supplies them.
 *
 * Primary reason keys are matched against the shared `TROUBLESHOOTING_REASONS`
 * map (constants/troubleshootingReasons), so a rename there can't leave this
 * file pointing at a reason that no longer exists. Subtype labels are string
 * literals mirrored from `BILLING_REASON_SUBTYPES` in that same module — the
 * spec pins every configured label against it, so a rename fails the build
 * instead of silently falling the subtype back to the primary pathway under an
 * unchanged chip.
 */
import { addDays, format } from 'date-fns';
import { TROUBLESHOOTING_REASONS } from './troubleshootingReasons';

/** One step of a reason's resolution pathway (one row of the workbook). */
export interface TroubleshootingStep {
  /** What the user does — the workbook's "Task Definition". */
  action: string;
  /** Target due date, verbatim from the workbook's "Task Due Date" column. */
  due?: string;
  /** The preceding outcome that selects this step — the workbook's "Task Outcome". */
  when?: string;
  /** The workbook's "Action after due date". */
  ifNotResolved?: string;
}

export interface ReasonPlaybook {
  /** The pathway for the primary reason. */
  steps: TroubleshootingStep[];
  /**
   * Subtype label (exactly as the Send to Troubleshooting dialog offers it) → the
   * pathway that REPLACES `steps` for that subtype. The workbook defines a pathway
   * per primary reason and only diverges for specific subtypes, so a subtype listed
   * here without an entry keeps the primary reason's steps.
   */
  subtypes?: Record<string, TroubleshootingStep[]>;
}

// ---- Shared steps (workbook sheet 2, "Task Definition") ----

const CONFIRM_NEXT_DUE_DATE: TroubleshootingStep = {
  action: 'Call the patient to obtain their next due / injection date',
  due: 'Today',
  // No ifNotResolved: the workbook models "patient unresponsive" as a Task #1
  // OUTCOME with its own due date, not an after-due-date action — its
  // "Action after due date" cell is empty (review, 2026-08-13).
};

const PSP_EMAIL_AND_PHONE: TroubleshootingStep = {
  action: 'Email AND phone the PSP to escalate',
  when: 'Patient is due in < 7 days',
  due: 'Today +1',
  ifNotResolved: 'Follow up with the PSP every day until they respond',
};

const PSP_EMAIL: TroubleshootingStep = {
  action: 'Email the PSP to escalate',
  when: 'Patient is due in > 7 days',
  due: 'Today +2',
  ifNotResolved:
    'Follow up every 2 days — move to Today +1 if the next injection date falls inside 7 days',
};

const NO_PSP_OUT_OF_POCKET: TroubleshootingStep = {
  action: 'Advise the patient they are responsible for the out-of-pocket cost',
  when: 'No PSP covers this patient',
  due: 'Today +2',
};

const PROCESS_AND_SCHEDULE: TroubleshootingStep = {
  action: 'Re-bill the claim and schedule the delivery',
  when: 'Issue resolved',
};

/**
 * Sheet 1's Task #1 → outcome → Task #2 chain: establish the patient's due date,
 * then escalate to the PSP on the timeline that due date dictates.
 */
const PSP_ESCALATION: TroubleshootingStep[] = [
  CONFIRM_NEXT_DUE_DATE,
  PSP_EMAIL_AND_PHONE,
  PSP_EMAIL,
  NO_PSP_OUT_OF_POCKET,
  PROCESS_AND_SCHEDULE,
];

/** Sheet 2's "Pt is unresponsive — Attempt 1..4" ladder, ending in cancellation. */
const UNREACHABLE_LADDER: TroubleshootingStep[] = [
  {
    action: 'Contact attempt 1 — patient is unresponsive',
    due: 'Today +1',
    ifNotResolved: 'Move to attempt 2',
  },
  {
    action: 'Contact attempt 2 — patient is unresponsive',
    due: 'Today +2',
    ifNotResolved: 'Move to attempt 3',
  },
  {
    action: 'Contact attempt 3 — patient is unresponsive',
    due: 'Today +7',
    ifNotResolved: 'Move to attempt 4',
  },
  {
    action: 'Contact attempt 4 — patient is unresponsive',
    due: 'Today + 100',
    ifNotResolved: 'Cancel the subscription',
  },
];

/** Sheet 2's "Trillium Max" tasks plus the letter hand-off. */
const TRILLIUM_STEPS: TroubleshootingStep[] = [
  CONFIRM_NEXT_DUE_DATE,
  {
    action: 'Trillium max — email the PSP',
    when: 'Patient is due in < 7 days',
    due: 'Today +1',
  },
  {
    action: 'Trillium max — email the PSP',
    when: 'Patient is due in > 7 days',
    due: 'Today +2',
  },
  {
    action: 'Forward the Trillium letter to the PSP',
    when: 'Trillium letter received from the patient',
    due: 'Today + 10',
  },
  PROCESS_AND_SCHEDULE,
];

// ---- Playbooks, keyed by TROUBLESHOOTING_REASONS key ----

export const BILLING_TROUBLESHOOTING_PLAYBOOKS: Record<string, ReasonPlaybook> = {
  insurance_rejection: {
    steps: PSP_ESCALATION,
    subtypes: {
      'Plan/Quantity Max Exceeded': [
        CONFIRM_NEXT_DUE_DATE,
        {
          action: 'Plan max — email the patient to request their max-out letter',
          when: 'Patient is due in > 2 weeks',
          due: 'Today +3',
        },
        // The workbook does not chain the PSP escalation after the max-out
        // letter for this subtype — no invented steps (review, 2026-08-13).
        PROCESS_AND_SCHEDULE,
      ],
      'Electronic Trillium Not going through': TRILLIUM_STEPS,
      'Trillium not in place': TRILLIUM_STEPS,
      'Coverage Terminated': [
        CONFIRM_NEXT_DUE_DATE,
        {
          action:
            'Plan termination — email the patient to request their max-out letter (attempt 1)',
          when: 'Patient is due in > 2 weeks',
          due: 'Today +3',
          ifNotResolved: 'Move to attempt 2',
        },
        {
          action:
            'Plan termination — email the patient to request their max-out letter (attempt 2)',
          due: 'Today +3',
          ifNotResolved: 'Patient unresponsive → follow the "Patient is Unreachable" pathway',
        },
        NO_PSP_OUT_OF_POCKET,
        PROCESS_AND_SCHEDULE,
      ],
      // SA Expired/EAP expired, Billing too early/Duplicate Claim, Mandatory
      // Biosimilar, Patient has other coverage and ODB Social Assistance Issue have
      // no subtype-specific tasks in the workbook — they run the PSP escalation above.
    },
  },

  lu_code: {
    // Sheet 2 rows "3-way call attempt" (Today +1) and "Fax for LU code Request"
    // (Today +2), in the workbook's own order.
    // The workbook's "Action after due date" cells for both rows are EMPTY —
    // no invented follow-ups (review, 2026-08-13).
    steps: [
      {
        action: 'Attempt a 3-way call with the patient and the prescriber',
        due: 'Today +1',
      },
      {
        action: 'Fax the LU code request to the prescriber',
        due: 'Today +2',
      },
      PROCESS_AND_SCHEDULE,
    ],
  },

  // DELIBERATELY ABSENT (review, 2026-08-13): `foc_approval_pending`,
  // `patient_payment_outstanding` and `refills_required` are named as primary
  // reasons in the workbook but have NO tasks defined for them — the ticket was
  // explicitly parked on "Workflow Map to be provided / Waiting on John to
  // share task names". Shipping invented steps here put un-authored operating
  // procedure (including a wrong out-of-pocket money conversation) in front of
  // staff with the authority of an on-screen SLA panel. Add them back ONLY
  // from the business's own task definitions.

  follow_up_with_patient: {
    // No primary tasks in the workbook (see note above) — the panel renders
    // nothing for the bare reason. The `Patient is unreachable` subtype IS
    // workbook-backed (sheet 1 R29 routes it into the unresponsive ladder).
    steps: [],
    subtypes: {
      'Patient is unreachable': UNREACHABLE_LADDER,
    },
  },

  patient_unreachable_billing: { steps: UNREACHABLE_LADDER },
};

// ---- Lookup ----

const normalize = (value: string): string => value.trim().toLowerCase().replace(/\s+/g, ' ');

/**
 * Normalized reason KEY or LABEL → reason key. A task's `troubleshootingReason` is
 * the human label the dialog wrote ("Insurance Rejection"), but a few backend paths
 * stamp the raw key, so both resolve. First registration wins, so the mapping is
 * deterministic if two reasons ever share a label.
 */
const REASON_KEY_BY_ALIAS: Record<string, string> = (() => {
  const index: Record<string, string> = {};
  for (const [key, label] of Object.entries(TROUBLESHOOTING_REASONS)) {
    for (const alias of [key, label]) {
      const normalized = normalize(alias);
      if (!(normalized in index)) index[normalized] = key;
    }
  }
  return index;
})();

export interface ResolvedPlaybook {
  reasonKey: string;
  reasonLabel: string;
  /** The subtype the reason was sent with, if any — shown even when it has no own steps. */
  subtypeLabel?: string;
  steps: TroubleshootingStep[];
}

/**
 * Resolve the steps for a task's stored troubleshooting reason.
 *
 * Accepts what the Send to Troubleshooting dialog actually writes: a label, a
 * `"Label | Subtype"` pair, or a raw reason key. Returns `null` — and the caller
 * renders nothing — when the reason is free text, or a reason the workbook doesn't
 * cover. Never falls back to another reason's pathway.
 */
export function getBillingTroubleshootingPlaybook(
  reason?: string | null,
): ResolvedPlaybook | null {
  if (!reason) return null;

  const [rawPrimary, ...rawSubtype] = reason.split('|');
  const reasonKey = REASON_KEY_BY_ALIAS[normalize(rawPrimary ?? '')];
  if (!reasonKey) return null;

  const playbook = BILLING_TROUBLESHOOTING_PLAYBOOKS[reasonKey];
  if (!playbook) return null;

  const subtypeLabel = rawSubtype.join('|').trim() || undefined;
  let steps = playbook.steps;
  if (subtypeLabel && playbook.subtypes) {
    const match = Object.entries(playbook.subtypes).find(
      ([label]) => normalize(label) === normalize(subtypeLabel),
    );
    if (match) steps = match[1];
  }

  // A reason whose workbook defines no primary tasks (steps: []) renders
  // nothing unless a subtype-specific pathway matched — never a borrowed one.
  if (steps.length === 0) return null;

  return {
    reasonKey,
    reasonLabel: TROUBLESHOOTING_REASONS[reasonKey],
    subtypeLabel,
    steps,
  };
}

/**
 * GP-1464 — turn a step's workbook due offset into the task's due date.
 *
 * The workbook writes offsets, not dates ("Today", "Today +1", "Today + 10"),
 * anchored on the day the step is started — which, for a task created the
 * moment the item is sent to troubleshooting, is today. Calendar days, not
 * business days: the workbook's own ladder ("Today +7", "Today + 100") counts
 * calendar days.
 *
 * Returns null for a step with no offset, or an offset in a shape this doesn't
 * recognize — the caller then leaves the due date unset and the backend applies
 * the Billing Troubleshooting task-type SLA. An invented date on a real
 * patient's claim would be worse than an SLA default.
 */
export function resolveStepDueDate(
  step: TroubleshootingStep | null | undefined,
  today: Date = new Date(),
): string | null {
  if (!step?.due) return null;
  const match = /^\s*today\s*(?:\+\s*([0-9]+))?\s*$/i.exec(step.due);
  if (!match) return null;
  const offsetDays = match[1] ? parseInt(match[1], 10) : 0;
  return format(addDays(today, offsetDays), 'yyyy-MM-dd');
}
